import React, { useEffect, useState } from 'react';
import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../auth/useAuth';
import { trackWrite } from '../../services/firebaseUsageTracker';
import type { AdminConfig } from '../AdminPanelModal';

type Props = {
  config: AdminConfig | null;
  configLoading: boolean;
  onConfigUpdate: (updates: Partial<AdminConfig>) => void;
};

export const AdminBranding: React.FC<Props> = ({ config, configLoading, onConfigUpdate }) => {
  const { profile, roles } = useAuth();
  const [casinoName, setCasinoName] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isAdmin = roles.includes('admin');
  const isDev = roles.includes('dev');

  // Sync local state with cached config when it loads
  useEffect(() => {
    if (config?.casinoName !== undefined) {
      setCasinoName(config.casinoName || '');
    } else if (profile?.casinoName) {
      // Fallback to profile if config doesn't have it
      setCasinoName(profile.casinoName);
    }
  }, [config, profile?.casinoName]);

  const handleSave = async () => {
    if (!profile?.uid) return;
    setSaving(true);
    setMessage(null);
    
    const trimmedName = casinoName.trim() || null;
    
    try {
      // Save to adminConfigs (primary source for invited users)
      const configRef = doc(db, 'adminConfigs', profile.uid);
      await setDoc(configRef, { casinoName: trimmedName }, { merge: true });
      trackWrite('adminConfigs', 'update-casino-name');
      
      // Also update user profile for legacy compatibility
      await updateDoc(doc(db, 'users', profile.uid), { casinoName: trimmedName });
      trackWrite('users', 'update-casino-name');
      
      // Update parent cache
      onConfigUpdate({ casinoName: trimmedName || undefined });
      
      setMessage({ type: 'success', text: 'Casino name saved!' });
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin && !isDev) {
    return <div className="text-center py-8 text-gray-400">Only admins can configure branding.</div>;
  }

  if (configLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4">
        <h3 className="text-lg font-semibold text-white mb-2">Casino Branding</h3>
        <p className="text-sm text-gray-400 mb-4">
          Set a custom casino name displayed to all users in your group.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Casino Name</label>
            <input
              type="text"
              className="w-full px-3 py-2 rounded-lg bg-gray-900 border border-gray-700 text-white focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              value={casinoName}
              onChange={e => setCasinoName(e.target.value)}
              placeholder="e.g., Lucky Stars Casino"
              maxLength={50}
              disabled={saving}
            />
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full px-4 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          {message && (
            <div className={`p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-emerald-900/50 border border-emerald-700 text-emerald-200' : 'bg-red-900/50 border border-red-700 text-red-200'}`}>
              {message.text}
            </div>
          )}
        </div>
      </div>
      <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4">
        <h3 className="text-lg font-semibold text-white mb-2">Preview</h3>
        <div className="bg-gray-900 rounded-lg p-4 border border-gray-700 text-center">
          <span className="text-2xl">🎰</span>
          <h4 className="text-xl font-bold text-white mt-2">{casinoName.trim() || 'Blackjack Trainer'}</h4>
        </div>
      </div>
    </div>
  );
};
